import React, { useState, useEffect } from 'react';
import { z } from 'zod';
import {
  useForm,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from './Form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Spinner } from './Spinner';
import { usePublishSite, usePublishedSites } from '../helpers/useCloudflareApi';
import { toast } from 'sonner';
import { ExternalLink, Globe, AlertCircle, CheckCircle2, Info, RefreshCw, Shield } from 'lucide-react';
import { SecurityScanPanel } from './SecurityScanPanel';
import { Selectable } from 'kysely';
import { SessionFiles } from '../helpers/schema';
import styles from './PublishSiteDialog.module.css';

interface PublishSiteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  sessionFiles: Selectable<SessionFiles>[];
  templateId?: string | null;
  /** True if this is a React/Vue/Svelte project that needs bundling before publish. */
  requiresBundling?: boolean;
}

const publishSchema = z.object({
  subdomain: z
    .string()
    .min(3, 'Subdomain must be at least 3 characters')
    .max(63, 'Subdomain must be at most 63 characters')
    .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens are allowed')
    .refine(
      (val) => !val.startsWith('-') && !val.endsWith('-'),
      'Cannot start or end with a hyphen',
    ),
  customDomain: z.string().optional(),
});

type PublishFormValues = z.infer<typeof publishSchema>;

export const PublishSiteDialog: React.FC<PublishSiteDialogProps> = ({
  open,
  onOpenChange,
  sessionId,
  sessionFiles,
  requiresBundling,
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [deployedUrl, setDeployedUrl] = useState<string | null>(null);
  const [securityErrors, setSecurityErrors] = useState(0);

  const publishMutation = usePublishSite();
  const { data: publishedSitesData } = usePublishedSites(sessionId);

  // Find existing published site for this session
  const existingSite =
    (publishedSitesData as any)?.sites?.find?.(
      (s: any) => s.sessionId === sessionId && s.deploymentStatus === 'deployed',
    ) || null;
  const isUpdate = !!existingSite;

  const [showCustomDomain, setShowCustomDomain] = useState(false);

  const form = useForm({
    defaultValues: {
      subdomain: '',
      customDomain: '',
    },
    schema: publishSchema,
  });

  // Pre-fill form with existing published site data when dialog opens
  useEffect(() => {
    if (open && existingSite) {
      form.setValues({
        subdomain: existingSite.subdomain || '',
        customDomain: existingSite.customDomain || '',
      });
      if (existingSite.customDomain) {
        setShowCustomDomain(true);
      }
    }
  }, [open, existingSite?.subdomain]);

  const handleSubmit = async (values: PublishFormValues) => {
    if (sessionFiles.length === 0) {
      toast.error('Cannot publish an empty session. Please create some files first.');
      return;
    }

    try {
      let filesPayload: { path: string; content: string }[];

      // Detect if this project needs bundling (React/Vue/Svelte/Angular)
      // Check the prop, file names, AND file contents
      const hasBundleableCode = requiresBundling || sessionFiles.some(f => {
        const name = f.fileName?.toLowerCase() || '';
        const content = f.content || '';
        return /\.(tsx|jsx)$/i.test(name) ||
          /from\s+['"]react/i.test(content) ||
          /from\s+['"]vue/i.test(content) ||
          /from\s+['"]svelte/i.test(content) ||
          name === 'vite.config.ts' || name === 'vite.config.js' || name === 'vite.config.mts';
      });

      console.log('[publish] sessionFiles count:', sessionFiles.length, 'fileNames:', sessionFiles.map(f => f.fileName).join(', '));
      console.log('[publish] requiresBundling:', requiresBundling, 'hasBundleableCode:', hasBundleableCode);

      // React/Vue/Svelte projects: run `vite build` in the WebContainer
      // and publish the production dist/ output — same as a real deploy.
      if (hasBundleableCode) {
        toast.info('Running production build...');
        const { buildForPublish } = await import('../lib/webcontainerPreview');
        const buildResult = await buildForPublish((line) => console.log(line));

        if (buildResult.errors.length > 0) {
          toast.error(`Build failed: ${buildResult.errors[0]}`);
          return;
        }
        if (buildResult.files.length === 0) {
          toast.error('Build produced no output files.');
          return;
        }

        filesPayload = buildResult.files;
      } else {
        // ── Plain HTML / vanilla JS project ──
        // Build a full path for each file combining filePath + fileName (same logic as ZIP download)
        filesPayload = sessionFiles
          .map((file) => {
            const fullPath =
              file.filePath === '/' || file.filePath === '' || !file.filePath
                ? file.fileName
                : `${file.filePath.replace(/^\//, '').replace(/\/$/, '')}/${file.fileName}`;
            return {
              path: fullPath as string,
              content: file.content || '',
            };
          })
          .filter((f) => f.path && f.path.trim() !== '');
      }

      if (filesPayload.length === 0) {
        toast.error('No files with valid paths to publish.');
        return;
      }

      // Ensure an index.html exists at the root or as an entry point
      if (!filesPayload.some((f) => /(^|\/)index\.(html|htm)$/i.test(f.path))) {
        toast.error(
          'Publish requires a root index.html (e.g. index.html). Please add one before publishing.',
        );
        return;
      }

      const result = await publishMutation.mutateAsync({
        sessionId,
        subdomain: values.subdomain,
        ...(values.customDomain ? { customDomain: values.customDomain } : {}),
        files: filesPayload,
      });

      // Always use .tasking.tech URL (the .pages.dev domain is just a CNAME)
      setDeployedUrl(`https://${values.subdomain}.tasking.tech`);
      setIsSuccess(true);
      toast.success(isUpdate ? 'Site updated successfully!' : 'Site published successfully!');
    } catch (error) {
      console.error('Failed to publish site:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to publish site');
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after a short delay to allow animation to finish
    setTimeout(() => {
      setIsSuccess(false);
      setDeployedUrl(null);
      setShowCustomDomain(false);
      // Only reset subdomain if there's no existing site
      if (!existingSite) {
        form.setValues({ subdomain: '', customDomain: '' });
      }
    }, 300);
  };

  const subdomain = form.values.subdomain;
  const previewUrl = subdomain
    ? `https://${subdomain}.tasking.tech`
    : 'https://your-name.tasking.tech';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.dialogContent}>
        <DialogHeader>
          <DialogTitle>{isUpdate ? 'Update Published Site' : 'Publish to Web'}</DialogTitle>
          <DialogDescription>
            {isUpdate
              ? 'Re-deploy your latest code to the existing published URL.'
              : 'Deploy your current session code to a public URL.'}
          </DialogDescription>
        </DialogHeader>

        {!isSuccess ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
              <div className={styles.previewCard}>
                <div className={styles.previewLabel}>
                  {isUpdate ? 'Published URL' : 'Public URL Preview'}
                </div>
                <div className={styles.previewUrl}>
                  <Globe size={16} className={styles.previewIcon} />
                  <span className={styles.urlText}>{previewUrl}</span>
                  {isUpdate && existingSite?.lastDeployedAt && (
                    <span
                      style={{
                        fontSize: '0.7rem',
                        color: 'var(--muted-foreground)',
                        marginLeft: 'auto',
                      }}
                    >
                      Last deployed: {new Date(existingSite.lastDeployedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              <FormItem name="subdomain">
                <FormLabel>Subdomain</FormLabel>
                <div className={styles.subdomainInputGroup}>
                  <FormControl>
                    <Input
                      placeholder="project-name"
                      value={form.values.subdomain}
                      onChange={(e) =>
                        form.setValues((prev) => ({ ...prev, subdomain: e.target.value }))
                      }
                      className={styles.subdomainInput}
                      disabled={isUpdate}
                    />
                  </FormControl>
                  <div className={styles.domainSuffix}>.tasking.tech</div>
                </div>
                {isUpdate ? (
                  <FormDescription>
                    Subdomain cannot be changed after publishing. To use a different subdomain,
                    publish as a new site.
                  </FormDescription>
                ) : (
                  <FormDescription>Lowercase letters, numbers, and hyphens only.</FormDescription>
                )}
                <FormMessage />
              </FormItem>

              <div style={{ marginTop: '0.25rem' }}>
                <button
                  type="button"
                  onClick={() => setShowCustomDomain(!showCustomDomain)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  {showCustomDomain ? 'Hide custom domain' : 'Add custom domain (CNAME)'}
                </button>
              </div>

              {showCustomDomain && (
                <FormItem name="customDomain">
                  <FormLabel>Custom Domain</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="myapp.example.com"
                      value={form.values.customDomain || ''}
                      onChange={(e) =>
                        form.setValues((prev) => ({ ...prev, customDomain: e.target.value }))
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    Point a CNAME record to{' '}
                    <strong>{form.values.subdomain || 'your-name'}.pages.dev</strong> at your DNS
                    provider. SSL is auto-provisioned by Cloudflare.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}

              {/* Security Scan */}
              <SecurityScanPanel
                files={sessionFiles.map(f => ({
                  path: f.filePath ? `${f.filePath.replace(/^\/|\/$/g, '')}/${f.fileName}` : f.fileName,
                  fileName: f.fileName,
                  filePath: f.filePath ?? undefined,
                  content: f.content ?? undefined,
                }))}
                onScanComplete={(issues) => setSecurityErrors(issues.filter(i => i.severity === 'error').length)}
              />

              {publishMutation.isError && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={16} />
                  <span>
                    {publishMutation.error instanceof Error
                      ? publishMutation.error.message
                      : 'An error occurred'}
                  </span>
                </div>
              )}

              <DialogFooter>
                <Button variant="ghost" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={publishMutation.isPending || !subdomain}
                  className={styles.publishButton}
                >
                  {publishMutation.isPending ? (
                    <>
                      <Spinner size="sm" className={styles.spinner} />
                      {isUpdate ? 'Updating...' : 'Publishing...'}
                    </>
                  ) : isUpdate ? (
                    <>
                      <RefreshCw size={14} />
                      Update Site
                    </>
                  ) : (
                    'Publish Site'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <div className={styles.successState}>
            <div className={styles.successIconWrapper}>
              <CheckCircle2 size={48} className={styles.successIcon} />
            </div>
            <h3 className={styles.successTitle}>
              {isUpdate ? 'Site Updated!' : 'Deployment Successful!'}
            </h3>
            <p className={styles.successMessage}>Your project is now live on the web.</p>

            <div className={styles.deployedUrlCard}>
              <a
                href={deployedUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.deployedLink}
              >
                {deployedUrl}
                <ExternalLink size={14} />
              </a>
            </div>

            <div className={styles.visitButtonWrapper}>
              <Button
                size="lg"
                onClick={() => window.open(deployedUrl || '#', '_blank')}
                className={styles.visitButton}
              >
                <ExternalLink size={16} />
                Visit Site
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(deployedUrl || '');
                  toast.success('URL copied to clipboard');
                }}
              >
                Copy URL
              </Button>
            </div>

            <DialogFooter className={styles.successFooter}>
              <Button
                variant="ghost"
                onClick={() => {
                  setIsSuccess(false);
                  setDeployedUrl(null);
                }}
              >
                Publish Another
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
